'use client';

import React, { useEffect, useState } from 'react';
import { Box, Typography, Container } from '@mui/material';
import People from '@/letsgo/icons/People';
import TrendingUp from '@/letsgo/icons/TrendingUp';

export function UserCount() {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('user-count');
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    let mounted = true;
    let rafId: number | null = null;

    (async () => {
      try {
        const res = await fetch('/api/user/count');
        if (!res.ok) throw new Error('fetch failed');
        const json = await res.json();
        const targetCount = Number(json?.count) || 0;
        if (!mounted) return;

        const reduceMotion =
          typeof window !== 'undefined' &&
          window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (reduceMotion || targetCount === 0) {
          setCount(targetCount);
          return;
        }

        const durationMs = 2000;
        const start = performance.now();
        const tick = (now: number) => {
          if (!mounted) return;
          const t = Math.min(1, (now - start) / durationMs);
          setCount(Math.min(targetCount, Math.floor(targetCount * t)));
          if (t < 1) {
            rafId = requestAnimationFrame(tick);
          } else {
            rafId = null;
            setCount(targetCount);
          }
        };
        rafId = requestAnimationFrame(tick);
      } catch {
        if (mounted) setCount(0);
      }
    })();

    return () => {
      mounted = false;
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [isVisible]);

  return (
    <Box
      id="user-count"
      component="section"
      role="region"
      aria-labelledby="user-count-heading"
      sx={{
        py: 4,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <People sx={{ fontSize: '2rem' }} aria-hidden />
          <Typography
            variant="h3"
            component="p"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-label={`Creators on FloAI: over ${count.toLocaleString()}`}
            suppressHydrationWarning
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            }}
          >
            {count.toLocaleString()}+
          </Typography>
          <TrendingUp sx={{ fontSize: '2rem', color: 'success.light' }} aria-hidden />
        </Box>

        <Typography id="user-count-heading" variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Creators Already Growing
        </Typography>
        
        <Typography variant="body1" sx={{ 
          opacity: 0.9,
          maxWidth: 'md',
          mx: 'auto'
        }}>
          Join thousands of content creators who are scaling their business with FloAI Studio
        </Typography>
      </Container>
    </Box>
  );
}
